import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./smooth";
import { useT } from "../../lib/content";
import { useCart } from "../../lib/cart";

const LINKS = [["galerie", "Galerie"], ["apropos", "L'artiste"], ["coeurs", null], ["exposition", "Exposition"], ["contact", "Contact"]];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useT();
  const cart = useCart();
  const links = LINKS.map(([id, l]) => [id, l ?? t("nav_hearts")]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const go = (id) => (e) => { e.preventDefault(); setOpen(false); scrollToId(id); };

  return (
    <>
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-[#F6F1E9]/85 backdrop-blur-xl shadow-[0_1px_0_rgba(21,32,43,0.08)]" : ""}`}>
      <div className={`bg-[#15202B] text-[#F6F1E9] text-center text-[10px] sm:text-xs tracking-[0.18em] uppercase overflow-hidden transition-[max-height,padding] duration-500 ${scrolled ? "max-h-0 py-0" : "max-h-12 py-2"}`} data-testid="announce-bar">
        {t("announce")}
      </div>
      <div className={`mx-auto transition-[padding] duration-500 ${scrolled ? "py-2" : "py-4"}`}>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between gap-6">
        <Logo onClick={go("top")} />
        <nav className="hidden lg:flex items-center gap-9" aria-label="Navigation principale">
          {links.map(([id, l]) => (
            <a key={id} href={`#${id}`} onClick={go(id)} data-testid={`nav-${id}-link`} className="nav-link text-[13px] tracking-[0.14em] uppercase text-[#15202B]">{l}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href="#galerie" onClick={go("galerie")} data-testid="nav-contact-cta"
            className="!hidden sm:!inline-flex btn-ink">{t("nav_cta")}</a>
          <button onClick={() => cart?.setOpen(true)} aria-label={`Panier (${cart?.count || 0})`} data-testid="nav-cart-button"
            className="relative w-11 h-11 grid place-items-center rounded-full border border-[#15202B]/20 hover:bg-[#15202B] hover:text-white transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {cart?.count > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#8A6420] text-white text-[11px] grid place-items-center tabular-nums" data-testid="nav-cart-count">{cart.count}</span>}
          </button>
          <button className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-[#15202B]/20" onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu" data-testid="nav-mobile-menu-button"><Menu className="w-5 h-5" /></button>
        </div>
      </div>
      </div>
    </header>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[70] bg-[#15202B] text-[#F6F1E9] flex flex-col px-6 pt-6 pb-10"
            initial={{ clipPath: "circle(0% at 92% 4%)" }} animate={{ clipPath: "circle(150% at 92% 4%)" }}
            exit={{ clipPath: "circle(0% at 92% 4%)" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="mobile-menu">
            <div className="flex justify-between items-center">
              <Logo light onClick={go("top")} testid="mobile-logo-link" />
              <button onClick={() => setOpen(false)} aria-label="Fermer le menu" data-testid="mobile-menu-close"
                className="w-11 h-11 grid place-items-center rounded-full border border-white/25"><X className="w-5 h-5" /></button>
            </div>
            <nav className="mt-14 flex flex-col gap-3">
              {links.map(([id, l], i) => (
                <motion.a key={id} href={`#${id}`} onClick={go(id)} data-testid={`mobile-nav-${id}-link`}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.06 }}
                  className="f-display text-5xl font-light">{l}</motion.a>
              ))}
            </nav>
            <p className="mt-auto text-sm text-slate-300">{t("announce")}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
